<script lang="ts">
	//Configs
	import { devices_config } from '@devices/index';
	//Helpers
	import {
		get_local_ip,
		get_mode,
		real_id,
		isMotion2Gen1,
	} from '../../../../helpers/interface_helpers';
	import { getActions, getAdcActions, get_external_sensor_actions } from './get_actions';
	//Helpers
	import { capFirstChar } from '../../../../helpers/utilities';
	import {
		getDeviceStatus,
		readDeviceSettings,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from 'svelte-i18n';
	//Hooks
	import { onMount } from 'svelte';
	//Components
	import ActionsV2 from '../WebHooksG1/ActionsV2.svelte';
	import QuickActionsWrapper from '../../../../components/QuickActions/QuickActionsWrapper.svelte';
	//Design system components
	import { Button, Loader } from '@Shelly/shelly-design-system';
	//Constants
	import { HALF_SLEEPING_TYPES, SLEEPING_TYPES } from '../../../../constants';
	import type { Device } from 'src/types/Device';

	$: statusStore = getDeviceStatus(device.id);
	export let device: Device,
		type = 'action';

	const settings = readDeviceSettings(device.id);
	const mode = get_mode(device, readDeviceStatus(device.id), settings);

	let loading = true;
	let localIp = get_local_ip(device.id),
		hideFromIoActions = devices_config[device.type].modes[mode].hideFromIoActions || [],
		hideFromSensorActions =
			devices_config[device.type]?.modes[device.category]?.hideFromSensorActions || [];

	let showModal = false;

	const isHalfAsleep = HALF_SLEEPING_TYPES.indexOf(device.type) !== -1 && !isMotion2Gen1(device.type);

	let deviceActions = {
		device,
		mode,
		options: [$format('window_open'), $format('temperature_report')],
		condition: $format('window_open'),
		category: undefined,
		callback: () => {},
		disabled: true,
		selectedDevices: [],
		loading: false,
		activeIndex: 0,
		selectedAction: undefined,
		type: undefined,
	};

	$: (deviceActions[device.id], _decodeActions(deviceActions[device.id]));

	const decode = (value: string) => {
		try {
			return decodeURIComponent(value);
		} catch (e) {
			return value;
		}
	};

	const _decodeActions = (actions) => {
		for (const actionName in actions) {
			if (actions.hasOwnProperty(actionName)) {
				const action = actions[actionName];
				for (let urlObj of action.urls) {
					if (urlObj?.url) {
						urlObj.url = decode(urlObj.url);
					} else if (urlObj) {
						urlObj = decode(urlObj);
					}
				}
			}
		}
	};

	const _getActions = async () => {
		let actions;
		let extHum = $statusStore.ext_humidity;
		let extTemp = $statusStore.ext_temperature;

		if (type === 'sensor') {
			if (SLEEPING_TYPES.includes(device.type)) {
				actions = await getActions(device, localIp, hideFromSensorActions);
			} else {
				actions = await get_external_sensor_actions(device, localIp, extTemp, extHum);
			}
		} else if (type === 'adc') {
			actions = await getAdcActions(device, localIp);
		} else {
			actions = await getActions(device, localIp, hideFromIoActions);
		}
		if (actions?.isok) {
			delete actions.isok;
		}

		deviceActions[device.id] = actions;

		loading = false;
	};
	onMount(async () => {
		let extSwitchActionNames = ['lp_on_url', 'lp_off_url'];
		if (device.category === 'relay' && !settings?.ext_switch_enable) {
			hideFromIoActions = [...hideFromIoActions, ...extSwitchActionNames];
		} else {
			hideFromIoActions = [...hideFromIoActions];
		}
		_getActions();
	});

	const updateActions = () => {
		_getActions();
	};
	const handleRetry = () => {
		loading = true;
		_getActions();
	};

	const callQuickActions = () => {
		showModal = true;
	};
</script>

{#if loading}
	<div class="d-flex justify-content-center">
		<Loader />
	</div>
{:else}
	<ActionsV2 bind:deviceActions {type} on:actionsUpdate={updateActions} on:retry={handleRetry} />

	{#if isHalfAsleep && Object.keys(deviceActions[device.id]).length > 0}
		<div class="pt-s">
			<Button
				size="m"
				width="full"
				variant="secondary"
				disabled={Object.keys(deviceActions[device.id]).length < 1}
				on:click={callQuickActions}
			>
				{capFirstChar($format('quick_actions', { default: 'Add quick actions' }))}
			</Button>
		</div>
	{/if}
{/if}

{#if showModal}
	<QuickActionsWrapper bind:deviceActions bind:showModal />
{/if}
